import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { getCookie } from './auth/cookies';
import { toast } from 'react-toastify';
import { 
  FaCloudUploadAlt, 
  FaFileAlt, 
  FaFileCode, 
  FaFileCsv, 
  FaFileArchive,
  FaTrashAlt,
  FaInfoCircle,
  FaUpload,
  FaStar
} from 'react-icons/fa';

export default function Submission() {
    const [predFiles, setPredFiles] = useState([]);  // For prediction files (CSV)
    const [trainFiles, setTrainFiles] = useState([]); // For training files (ZIP or other) - OPTIONAL
    const [finalFiles, setFinalFiles] = useState([]); // For final prediction files (CSV) - OPTIONAL
    const [isDraggingPred, setIsDraggingPred] = useState(false);
    const [isDraggingTrain, setIsDraggingTrain] = useState(false);
    const [isDraggingFinal, setIsDraggingFinal] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submissionContent, setSubmissionContent] = useState(null);
    
    // References for file inputs
    const predInputRef = useRef(null);
    const trainInputRef = useRef(null);
    const finalInputRef = useRef(null);

    // Fetch submission content
    const fetchSubmissionContent = async () => {
        try {
            const response = await axios.get('/api/load_submission_content/', {
                headers: { 'X-CSRFToken': getCookie('csrftoken') }
            });
            setSubmissionContent(response.data.content);
        } catch (err) {
            console.error('Error loading submission content:', err);
            toast.error(`Error loading submission content: ${err.message}`);
        }
    };

    useEffect(() => {
        fetchSubmissionContent();
    }, []);

    // Get file icon based on file type
    const getFileIcon = (fileName) => {
        const extension = fileName.split('.').pop().toLowerCase();
        
        if (['csv'].includes(extension)) return <FaFileCsv className="text-green-500" />;
        if (['zip', 'rar', 'tar', 'gz'].includes(extension)) return <FaFileArchive className="text-yellow-500" />;
        if (['py', 'js', 'java', 'c', 'cpp', 'r', 'ipynb', 'html', 'css'].includes(extension)) return <FaFileCode className="text-blue-500" />;
        return <FaFileAlt className="text-gray-500" />;
    };

    // Format file size to human-readable format
    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    // Calculate total size of files
    const calculateTotalSize = (files) => {
        return files.reduce((total, file) => total + file.size, 0);
    };

    // Handle file drop for predictions
    const handleDropPred = (e) => {
        e.preventDefault();
        setIsDraggingPred(false);
        const newFiles = Array.from(e.dataTransfer.files);
        
        // Filter for CSV files
        const csvFiles = newFiles.filter(file => 
            file.name.toLowerCase().endsWith('.csv')
        );
        
        if (csvFiles.length !== newFiles.length) {
            toast.warning('Only CSV files are allowed for predictions. Non-CSV files were removed.');
        }
        
        setPredFiles(csvFiles);
    };

    // Handle file drop for training files
    const handleDropTrain = (e) => {
        e.preventDefault();
        setIsDraggingTrain(false);
        const newFiles = Array.from(e.dataTransfer.files);
        const totalSize = calculateTotalSize(newFiles);
        
        if (totalSize > 100 * 1024 * 1024) { // 100MB limit
            toast.error('Total file size exceeds 100MB. Please reduce the file size.');
        } else {
            setTrainFiles(newFiles);
        }
    };

    // Handle file drop for final predictions
    const handleDropFinal = (e) => {
        e.preventDefault();
        setIsDraggingFinal(false);
        const newFiles = Array.from(e.dataTransfer.files);
        
        // Filter for CSV files
        const csvFiles = newFiles.filter(file => 
            file.name.toLowerCase().endsWith('.csv')
        );
        
        if (csvFiles.length !== newFiles.length) {
            toast.warning('Only CSV files are allowed for final predictions. Non-CSV files were removed.');
        }
        
        setFinalFiles(csvFiles);
    };

    // Handle drag events
    const handleDragOverPred = (e) => {
        e.preventDefault();
        setIsDraggingPred(true);
    };

    const handleDragLeavePred = () => {
        setIsDraggingPred(false);
    };

    const handleDragOverTrain = (e) => {
        e.preventDefault();
        setIsDraggingTrain(true);
    };

    const handleDragLeaveTrain = () => {
        setIsDraggingTrain(false);
    };

    const handleDragOverFinal = (e) => {
        e.preventDefault();
        setIsDraggingFinal(true);
    };

    const handleDragLeaveFinal = () => {
        setIsDraggingFinal(false);
    };

    // Trigger file input click
    const triggerPredFileInput = () => {
        predInputRef.current.click();
    };
    
    const triggerTrainFileInput = () => {
        trainInputRef.current.click();
    };

    const triggerFinalFileInput = () => {
        finalInputRef.current.click();
    };

    // Handle file selection (via input) for predictions
    const handleFileSelectPred = (e) => {
        const selectedFiles = Array.from(e.target.files);
        
        // Filter for CSV files
        const csvFiles = selectedFiles.filter(file => 
            file.name.toLowerCase().endsWith('.csv')
        );
        
        if (csvFiles.length !== selectedFiles.length) {
            toast.warning('Only CSV files are allowed for predictions. Non-CSV files were removed.');
        }
        
        setPredFiles(csvFiles);
    };

    // Handle file selection for training files
    const handleFileSelectTrain = (e) => {
        const selectedFiles = Array.from(e.target.files);
        const totalSize = calculateTotalSize(selectedFiles);

        if (totalSize > 100 * 1024 * 1024) { // 100MB limit
            toast.error('Total file size exceeds 100MB. Please reduce the file size.');
        } else {
            setTrainFiles(selectedFiles);
        }
    };

    // Handle file selection for final predictions
    const handleFileSelectFinal = (e) => {
        const selectedFiles = Array.from(e.target.files);
        
        // Filter for CSV files
        const csvFiles = selectedFiles.filter(file => 
            file.name.toLowerCase().endsWith('.csv')
        );
        
        if (csvFiles.length !== selectedFiles.length) {
            toast.warning('Only CSV files are allowed for final predictions. Non-CSV files were removed.');
        }
        
        setFinalFiles(csvFiles);
    };

    // Remove a file from the prediction files list
    const removePredFile = (index) => {
        setPredFiles(predFiles.filter((_, i) => i !== index));
    };

    // Remove a file from the training files list
    const removeTrainFile = (index) => {
        setTrainFiles(trainFiles.filter((_, i) => i !== index));
    };

    // Remove a file from the final prediction files list
    const removeFinalFile = (index) => {
        setFinalFiles(finalFiles.filter((_, i) => i !== index));
    };

    // Handle form submission and file upload
    const handleSubmit = async () => {
        // Only prediction files are required now
        if (predFiles.length === 0) {
            // toast.warning('Please select at least one prediction file.');
            // return;
        }

        // Verify prediction file has CSV extension
        const hasCsvFile = predFiles.some(file => file.name.toLowerCase().endsWith('.csv'));
        if (!hasCsvFile) {
            // toast.error('Please include a CSV file for predictions.');
            // return;
        }

        // FINAL files: now required
        if (finalFiles.length === 0) {
            toast.warning('Please upload at least one Final Predictions CSV.');
            return;
        }
        const hasCsvFinal = finalFiles.some(f => f.name.toLowerCase().endsWith('.csv'));
        if (!hasCsvFinal) {
            toast.error('Please include a CSV file for final predictions.');
            return;
        }

        const formData = new FormData();

        // Append each prediction file to the form data
        predFiles.forEach((file) => {
            formData.append('predFiles', file);
        });

        // Append training files only if they exist
        if (trainFiles.length > 0) {
            trainFiles.forEach((file) => {
                formData.append('trainFiles', file);
            });
        }

        // Append final files only if they exist
        if (finalFiles.length > 0) {
            finalFiles.forEach((file) => {
                formData.append('finalFiles', file);
            });
        }

        setIsSubmitting(true);
        const toastId = toast.loading('Uploading files...');
        
        try {
            const response = await axios.post('/api/submit_score/', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'X-CSRFToken': getCookie('csrftoken')
                },
            });
            
            toast.update(toastId, { 
                render: 'Files uploaded successfully!', 
                type: 'success', 
                isLoading: false, 
                autoClose: 4000 
            });
            
            // Clear file selections after successful upload
            setPredFiles([]);
            setTrainFiles([]);
            setFinalFiles([]);
            
        } catch (error) {
            const errorMessage = error.response?.data?.error || 'An error occurred during upload.';
            toast.update(toastId, { 
                render: errorMessage, 
                type: 'error', 
                isLoading: false, 
                autoClose: 5000 
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="bg-white dark:bg-gray-900 min-h-screen text-gray-900 dark:text-gray-200 pt-20">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
                {/* Header Section */}
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-secondary-dark dark:text-white mb-4">
                        {submissionContent?.header?.title || 'Submit Your Results'}
                    </h1>
                    <div className="bg-blue-50 dark:bg-blue-900/30 border-l-4 border-blue-500 p-4 rounded-r-md">
                        <div className="flex">
                            <div className="flex-shrink-0">
                                <FaInfoCircle className="h-5 w-5 text-blue-500" />
                            </div>
                            <div className="ml-3">
                                <p className="text-sm text-blue-700 dark:text-blue-300">
                                    {submissionContent?.header?.instruction || 'Create a CSV file containing the columns "id" and "preds" with your predictions and submit it. Training files and final predictions are optional. Results will be updated on the leaderboard within seconds of submission.'}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
                
                <div className="grid lg:grid-cols-3 md:grid-cols-2 gap-8">
                    {/* Predictions Upload Section - REQUIRED */}
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
                        <div className="bg-green-600 px-4 py-3">
                            <h2 className="text-lg font-semibold text-white flex items-center">
                                <FaFileCsv className="mr-2" /> 
                                {submissionContent?.upload_sections?.prediction_files?.title || 'Prediction Files (CSV)'} 
                                <span className="ml-2 text-xs bg-green-800 px-2 py-1 rounded">Optional</span>
                            </h2>
                        </div>
                        
                        <div className="p-4">
                            <div
                                className={`
                                    w-full h-48 p-4 border-2 border-dashed rounded-lg 
                                    flex flex-col justify-center items-center cursor-pointer
                                    transition-all duration-200
                                    ${isDraggingPred 
                                        ? 'border-green-400 dark:border-green-500 bg-green-50 dark:bg-green-900/20' 
                                        : 'border-gray-300 dark:border-gray-600 hover:border-green-300 dark:hover:border-green-700'}
                                `}
                                onDragOver={handleDragOverPred}
                                onDragLeave={handleDragLeavePred}
                                onDrop={handleDropPred}
                                onClick={triggerPredFileInput}
                            >
                                <input 
                                    type="file" 
                                    multiple
                                    ref={predInputRef}
                                    className="hidden"
                                    onChange={handleFileSelectPred}
                                    accept=".csv"
                                />
                                
                                <FaCloudUploadAlt className="text-4xl mb-3 text-gray-400 dark:text-gray-500" />
                                <p className="text-gray-500 dark:text-gray-400 text-center">
                                    {submissionContent?.upload_sections?.prediction_files?.drag_drop_text || 'Drag and drop CSV files here, or click to browse'}
                                </p>
                                <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
                                    {submissionContent?.upload_sections?.prediction_files?.requirement || 'Must include a column named "preds"'}
                                </p>
                            </div>
                            
                            {/* File list for predictions */}
                            {predFiles.length > 0 && (
                                <div className="mt-4">
                                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Selected Files ({predFiles.length})
                                    </h3>
                                    <ul className="divide-y divide-gray-200 dark:divide-gray-700 max-h-40 overflow-y-auto">
                                        {predFiles.map((file, index) => (
                                            <li key={index} className="py-2 flex items-center justify-between">
                                                <div className="flex items-center">
                                                    {getFileIcon(file.name)}
                                                    <span className="ml-2 text-sm text-gray-600 dark:text-gray-300 truncate max-w-xs">
                                                        {file.name}
                                                    </span>
                                                    <span className="ml-2 text-xs text-gray-400 dark:text-gray-500">
                                                        {formatFileSize(file.size)}
                                                    </span>
                                                </div>
                                                <button 
                                                    className="text-red-500 hover:text-red-700 dark:hover:text-red-400 p-1" 
                                                    onClick={() => removePredFile(index)}
                                                    title="Remove file"
                                                >
                                                    <FaTrashAlt />
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Training Files Upload Section - OPTIONAL */}
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
                        <div className="bg-blue-600 px-4 py-3">
                            <h2 className="text-lg font-semibold text-white flex items-center">
                                <FaFileCode className="mr-2" /> 
                                {submissionContent?.upload_sections?.training_files?.title || 'Training Files'} 
                                <span className="ml-2 text-xs bg-blue-800 px-2 py-1 rounded">Optional</span>
                            </h2>
                        </div>
                        
                        <div className="p-4">
                            <div
                                className={`
                                    w-full h-48 p-4 border-2 border-dashed rounded-lg 
                                    flex flex-col justify-center items-center cursor-pointer
                                    transition-all duration-200
                                    ${isDraggingTrain 
                                        ? 'border-blue-400 dark:border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
                                        : 'border-gray-300 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-700'}
                                `}
                                onDragOver={handleDragOverTrain}
                                onDragLeave={handleDragLeaveTrain}
                                onDrop={handleDropTrain}
                                onClick={triggerTrainFileInput}
                            >
                                <input 
                                    type="file" 
                                    multiple
                                    ref={trainInputRef}
                                    className="hidden"
                                    onChange={handleFileSelectTrain}
                                />
                                
                                <FaCloudUploadAlt className="text-4xl mb-3 text-gray-400 dark:text-gray-500" />
                                <p className="text-gray-500 dark:text-gray-400 text-center">
                                    {submissionContent?.upload_sections?.training_files?.drag_drop_text || 'Drag and drop your code or model files here, or click to browse'}
                                </p>
                                <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
                                    {submissionContent?.upload_sections?.training_files?.requirement || 'ZIP files recommended for multiple files (100MB max)'}
                                </p>
                            </div>
                            
                            {/* File list for training files */}
                            {trainFiles.length > 0 && (
                                <div className="mt-4">
                                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Selected Files ({trainFiles.length}) - 
                                        <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">
                                            Total: {formatFileSize(calculateTotalSize(trainFiles))}
                                        </span>
                                    </h3>
                                    <ul className="divide-y divide-gray-200 dark:divide-gray-700 max-h-40 overflow-y-auto">
                                        {trainFiles.map((file, index) => (
                                            <li key={index} className="py-2 flex items-center justify-between">
                                                <div className="flex items-center">
                                                    {getFileIcon(file.name)}
                                                    <span className="ml-2 text-sm text-gray-600 dark:text-gray-300 truncate max-w-xs">
                                                        {file.name}
                                                    </span>
                                                    <span className="ml-2 text-xs text-gray-400 dark:text-gray-500">
                                                        {formatFileSize(file.size)}
                                                    </span>
                                                </div>
                                                <button 
                                                    className="text-red-500 hover:text-red-700 dark:hover:text-red-400 p-1" 
                                                    onClick={() => removeTrainFile(index)}
                                                    title="Remove file"
                                                >
                                                    <FaTrashAlt />
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Final Predictions Upload Section - OPTIONAL */}
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
                        <div className="bg-purple-600 px-4 py-3">
                            <h2 className="text-lg font-semibold text-white flex items-center">
                                <FaStar className="mr-2" /> 
                                {submissionContent?.upload_sections?.final_predictions?.title || 'Final Predictions (CSV)'} 
                                <span className="ml-2 text-xs bg-purple-800 px-2 py-1 rounded">Required</span>
                            </h2>
                        </div>
                        
                        <div className="p-4">
                            <div
                                className={`
                                    w-full h-48 p-4 border-2 border-dashed rounded-lg 
                                    flex flex-col justify-center items-center cursor-pointer
                                    transition-all duration-200
                                    ${isDraggingFinal 
                                        ? 'border-purple-400 dark:border-purple-500 bg-purple-50 dark:bg-purple-900/20' 
                                        : 'border-gray-300 dark:border-gray-600 hover:border-purple-300 dark:hover:border-purple-700'}
                                `}
                                onDragOver={handleDragOverFinal}
                                onDragLeave={handleDragLeaveFinal}
                                onDrop={handleDropFinal}
                                onClick={triggerFinalFileInput}
                            >
                                <input 
                                    type="file" 
                                    multiple
                                    ref={finalInputRef}
                                    className="hidden"
                                    onChange={handleFileSelectFinal}
                                    accept=".csv"
                                />
                                
                                <FaCloudUploadAlt className="text-4xl mb-3 text-gray-400 dark:text-gray-500" />
                                <p className="text-gray-500 dark:text-gray-400 text-center">
                                    {submissionContent?.upload_sections?.final_predictions?.drag_drop_text || 'Drag and drop final prediction CSV files here, or click to browse'}
                                </p>
                                <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
                                    {submissionContent?.upload_sections?.final_predictions?.requirement || 'Final submission predictions'}
                                </p>
                            </div>
                            
                            {/* File list for final predictions */}
                            {finalFiles.length > 0 && (
                                <div className="mt-4">
                                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Selected Files ({finalFiles.length})
                                    </h3>
                                    <ul className="divide-y divide-gray-200 dark:divide-gray-700 max-h-40 overflow-y-auto">
                                        {finalFiles.map((file, index) => (
                                            <li key={index} className="py-2 flex items-center justify-between">
                                                <div className="flex items-center">
                                                    {getFileIcon(file.name)}
                                                    <span className="ml-2 text-sm text-gray-600 dark:text-gray-300 truncate max-w-xs">
                                                        {file.name}
                                                    </span>
                                                    <span className="ml-2 text-xs text-gray-400 dark:text-gray-500">
                                                        {formatFileSize(file.size)}
                                                    </span>
                                                </div>
                                                <button 
                                                    className="text-red-500 hover:text-red-700 dark:hover:text-red-400 p-1" 
                                                    onClick={() => removeFinalFile(index)}
                                                    title="Remove file"
                                                >
                                                    <FaTrashAlt />
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                
                {/* Additional Instructions */}
                <div className="mt-8 bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
                    <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center">
                        <FaInfoCircle className="mr-2 text-blue-500" /> {submissionContent?.important_info?.title || 'Important Information'}
                    </h3>
                    <ul className="list-disc pl-5 space-y-2 text-gray-600 dark:text-gray-400">
                        {submissionContent?.important_info?.items ? (
                            submissionContent.important_info.items.map((item, index) => (
                                <li key={index} dangerouslySetInnerHTML={{ __html: item }} />
                            ))
                        ) : (
                            <>
                                <li>Your prediction CSV files <strong>must</strong> include the columns "id" and "preds" with file name without extension and your predictions</li>
                                <li><strong>Training files are optional</strong> - include code, datasets, or any materials used to train your model if desired</li>
                                <li><strong>Final predictions are optional</strong> - submit only if you have final test set predictions</li>
                                <li>All submissions will be reviewed by an administrator to verify legitimacy and compliance</li>
                                <li>After submission, your results will appear on the leaderboard within seconds</li>
                                <li>Total file size limit is <strong>100MB</strong></li>
                            </>
                        )}
                    </ul>
                </div>

                {/* Submit Button */}
                <div className="mt-8 flex justify-center">
                    <button
                        className={`
                            px-8 py-4 rounded-lg font-semibold text-white 
                            flex items-center justify-center
                            transition-all duration-300
                            ${(isSubmitting)
                                ? 'bg-gray-400 dark:bg-gray-600 cursor-not-allowed'
                                : 'bg-secondary hover:bg-secondary-dark shadow-lg hover:shadow-xl'}
                        `}
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? (
                            <>
                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Submitting...
                            </>
                        ) : (
                            <>
                                <FaUpload className="mr-2" /> {submissionContent?.buttons?.submit || 'Submit Results'}
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
}